import { inject, Injectable } from "@angular/core";
import { catchError, filter, finalize, Observable, of, switchMap, tap } from "rxjs";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { InterfaceStatus } from "src/app/shared/contract/Enum/Status";
import { NotificationService } from "src/app/shared/services/notification.service";
import { HttpClient } from "@angular/common/http";
import { InitService } from "@services/init.service";
import { CommercialAVMRequest } from "../../contract/CommercialRealEstate/CommercialAVMRequest";
import { Address } from "../../contract/Address/Address";
import { ObjectInformation } from "../../contract/ObjectInformation/ObjectInformation";
import { LoadingService } from "../../../modules/loading/loading.service";
import { DataModeService } from "../../../modules/menu/data-mode.service";
import { RealEstateStoreService } from "../../../modules/real-estate/services/real-estate-store.service";
import { RealEstateBaseObject } from "../../../modules/real-estate/models/real-estate-base-object.interface";
import { EstateType } from "../../contract/Enum/portfolio.enum";
import { AccumateModule } from "../../consts/icon-features";
import { ObjectInformationEditDialogComponent } from "../../../modules/object-information/object-information-edit-dialog/object-information-edit-dialog.component";
import { ObjectInformationEditDialogData } from "@models/object-information-dialog-data.interface";
import { ObjectInformationEditDialogResult } from "@models/object-information-edit-dialog-result.interface";

@Injectable({
  providedIn: "root"
})
export class RealEstateService {
  private readonly httpClient = inject(HttpClient);
  private readonly initService = inject(InitService);
  private readonly loadingService = inject(LoadingService);
  private readonly dataModeService = inject(DataModeService);
  private readonly matDialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);
  private readonly realEstateStoreService = inject(RealEstateStoreService);

  private readonly commercialRealEstateUrl = "/api/AVM/Commercial";
  private readonly residentialRealEstateUrl = "/api/AVM/Residential";

  createNewRealEstateObject(
    isEditAddressAllowed: boolean,
    estateType: EstateType,
    address?: Address,
    objectInformation?: ObjectInformation,
    customerId?: string
  ): Observable<RealEstateBaseObject | undefined> {
    const dialogConfig: MatDialogConfig<ObjectInformationEditDialogData> = {
      data: {
        isNewObject: true,
        isLocked: false,
        isCommercial: estateType === EstateType.Commercial,
        isEditAddressAllowed: isEditAddressAllowed,
        address: address,
        objectInformation: objectInformation,
        customerId: customerId
      }
    };

    const dialogRef = this.matDialog.open<
      ObjectInformationEditDialogComponent,
      ObjectInformationEditDialogData,
      ObjectInformationEditDialogResult
    >(ObjectInformationEditDialogComponent, dialogConfig);

    return dialogRef.afterClosed().pipe(
      filter(createDialogResult => !!createDialogResult),
      switchMap(createDialogResult => {
        const request: CommercialAVMRequest = {
          address: createDialogResult.address,
          objectInformation: createDialogResult.objectInformation,
          customerId: createDialogResult.customerId
        };

        this.loadingService.show();

        return this.getRealEstateObject(request, estateType);
      }),
      catchError(err => {
        throw new Error(err);
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  loadRealEstateObject(realEstateRequest, estateType: EstateType, accumateModule: AccumateModule) {
    return this.getRealEstateObject(realEstateRequest, estateType).pipe(
      filter(realEstateResponse => {
        if (realEstateResponse?.status !== InterfaceStatus.OK) {
          this.handleInterfaceError(realEstateResponse?.status);
          return false;
        }
        return true;
      }),
      tap(realEstateResponse => this.realEstateStoreService.setRealEstate(realEstateResponse, accumateModule)),
      catchError(err => {
        console.error(err);
        this.notificationService.defaultError();
        return of(false);
      })
    );
  }

  handleInterfaceError(interfaceStatus: InterfaceStatus) {
    if (interfaceStatus !== InterfaceStatus.OK) {
      this.notificationService.defaultError();
      this.dataModeService.resetSelectedDataMode();
    }
  }

  getRealEstateObject(data, estateType: EstateType) {
    const realEstateUrl = estateType === EstateType.Commercial ? this.commercialRealEstateUrl : this.residentialRealEstateUrl;
    return this.httpClient.post<RealEstateBaseObject>(this.initService.getApiUrl(realEstateUrl), data);
  }
}
